import React from 'react'
// libs
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
// components
import PublishIcon from '@mui/icons-material/Publish'
import { IconButton } from 'components/UI'
// utils
import pluralize from 'pluralize'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

const FileInput = styled.input`
  display: none;
`

interface IBulkImportItem {
  code: string
  description: string
  parentCode: string
}

interface IProps {
  label?: string
  mutation: (options: { variables: { input: { items: IBulkImportItem[] } } }) => Promise<unknown>
  onImported: () => Promise<unknown> | unknown
}

const HEADER_MATCHERS = [/code/i, /description/i, /parent/i]

const isHeaderRow = (row: string[]): boolean =>
  HEADER_MATCHERS.some((matcher, index) => matcher.test(String(row[index] ?? '')))

const toCellString = (value: unknown): string => String(value ?? '').trim();

const parseRowsToItems = (rows: unknown[][]): IBulkImportItem[] =>
  rows
    .filter((row) => Array.isArray(row) && row.some((cell) => toCellString(cell) !== ''))
    .filter((row, index) => !(index === 0 && isHeaderRow(row as string[])))
    .map((row) => ({
      code: toCellString(row[0]),
      description: toCellString(row[1]),
      parentCode: toCellString(row[2])
    }))

const ImportCsvButton: React.FC<IProps> = ({ label = 'Import', mutation, onImported }) => {
  const inputFileRef = React.useRef<HTMLInputElement>(null)

  const [isLoading, setIsLoading] = React.useState(false)

  const { enqueueSnackbar } = useSnackbar()

  const onButtonClick = () => inputFileRef.current?.click()

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    event.target.value = null

    if (!file) {
      return
    }

    setIsLoading(true)

    try {
      const buffer = await file.arrayBuffer()
      const xlsx = await import('@e965/xlsx')
      const workbook = xlsx.read(buffer, { type: 'array' })
      const worksheet = workbook.Sheets[workbook.SheetNames[0]]
      const rows = xlsx.utils.sheet_to_json<unknown[]>(worksheet, { header: 1, blankrows: false })

      const items = parseRowsToItems(rows)

      if (!items.length) {
        enqueueSnackbar('No rows found in the file', { variant: 'warning' })
        return
      }

      await mutation({ variables: { input: { items } } })

      await onImported()

      enqueueSnackbar(`Imported ${items.length} ${pluralize('row', items.length)}`, { variant: 'success' })
    } catch (error) {
      console.log('[ImportCsvButton.onFileChange]:', parseGraphQLErrorMessages(error))
      enqueueSnackbar('Failed to import file', { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <IconButton preset='PURPLE' isActive description={label} disabled={isLoading} onClick={onButtonClick}>
        <PublishIcon />
      </IconButton>

      <FileInput
        type='file'
        ref={inputFileRef}
        onChange={onFileChange}
        accept='text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .csv'
      />
    </>
  )
}

export default ImportCsvButton
